import { useState ,useEffect} from "react";
import { Box, Button, FormControlLabel, Switch, TextField, Grid, FormControl } from "@mui/material";
import { ReportFilterViewModel,Option } from "../../Types/ExportTypes";
import { CustomerAutoComplete, SupplierAutoComplete, Dimension1AutoComplete, CountryAutoComplete, LocationAutoComplete, ShopAutoComplete, SalespersonAutoComplete, ItemAutoComplete, ItemGroupAutoComplete, ProgramAutoComplete, CollectionAutoComplete } from "../../components/AutoComplete/AutoCompletes";
import AsyncAutoComplete from "../../components/AsyncAutoComplete";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../Context/Contexts";
import { useTranslation } from "react-i18next";
import ItemCategoryProductGroupSelect from "../../components/Item/ItemCategoryProductGroupSelect";
interface ReportHeaderProps {
    filter: ReportFilterViewModel;
    onGenerate?: (filter: ReportFilterViewModel) => void;
    skipFilters?: string[];
}

const ReportHeader: React.FC<ReportHeaderProps> = ({ filter, onGenerate, skipFilters }) => {
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [localFilter, setLocalFilter] = useState<ReportFilterViewModel>(filter);

    const { authService } = useAuth();
    const { goBack, navigate } = useNavigation();
    const { showSnackbar } = useSnackbar();
    const { showLoading, hideLoading } = useLoading();
    const [itemCategories, setItemCategories] = useState<Option[]>([]);
    const [productGroups, setProductGroups] = useState<Option[]>([]);
    const [loadedOptions, setloadedOptions] = useState(false);
    const { t } = useTranslation();


    const handleFilterChange = (key: keyof ReportFilterViewModel, value: string | null) => {
        setLocalFilter((prev) => ({ ...prev, [key]: value }));
    };


    const handleChanges = (changes: Partial<ReportFilterViewModel>) => {
        setLocalFilter((prev) => {
            const updatedItem = {
                ...prev,
                ...changes
            };
            return updatedItem;
        });
    };


    const handleGenerate = () => {
        onGenerate?.(localFilter);
    };

    const handleClear = () => {
        setLocalFilter({});
    };


    const fetchItemCategoryOptions = async (
        search: string,
        offset: number,
        code?: string
    ): Promise<Option[]> => {
        const url = code
            ? `/autocompleteApi/ItemCategory?code=${code}`
            : `/autocompleteApi/ItemCategory?search=${search}&offset=${offset}&limit=50`;

        const response = await authService.get<Option[]>(url);
        return response || [];
    };

    // Fetch function for ProductGroup with dependency on ItemCategoryCode
    const fetchProductGroupOptions = async (
        search: string,
        offset: number,
        code?: string,
        itemCategoryCode?: string |null
    ): Promise<Option[]> => {
        if (!filter.itemCategory) return [];
        if (code) {
            const response = await authService.get<Option[]>(
                `/autocompleteApi/ProductGroup?itemCategoryCode=${itemCategoryCode ?? filter.itemCategory}&code=${code}`
            );
            return response || [];
        }



        const response = await authService.get<Option[]>(
            `/autocompleteApi/ProductGroup?itemCategoryCode=${itemCategoryCode ?? filter.itemCategory}&search=${search}&offset=${offset}&limit=50`
        );
        return response || [];
    };

    const initializeDropdowns = async () => {
        try {
            showLoading();
            const [categories, groups] = await Promise.all([
                fetchItemCategoryOptions("", 0),
                fetchProductGroupOptions("", 0),

            ]);

            setItemCategories(categories || []);
            setProductGroups(groups || []);

            //  setPurchaseCurrencyList(currencyList || []);
        } catch (error) {
            console.error("Failed to initialize dropdown options:", error);
        }
        finally {
            hideLoading();
        }
    };

    useEffect(() => {
        if (loadedOptions === false) {
            initializeDropdowns();
            setloadedOptions(true);
        }
    }, [initializeDropdowns, loadedOptions]);


    return (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                    <TextField
                        label={t("common.dateFrom")}
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        value={localFilter.dateFrom || ""}
                        onChange={(e) => handleFilterChange("dateFrom", e.target.value || null)}
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField
                        label={t("common.dateTo")}
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        value={localFilter.dateTo || ""}
                        onChange={(e) => handleFilterChange("dateTo", e.target.value || null)}
                    />
                </Grid>
                {!skipFilters?.includes("advanced") && (
                    <Grid item xs={12} sm={3}>
                        <FormControlLabel
                            control={<Switch checked={showAdvanced} onChange={() => setShowAdvanced(!showAdvanced)} />}
                            label={t("common.advancedFilter")}
                        />
                    </Grid>
                )}
        
            </Grid>
            
            { showAdvanced && (
                <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                        {!skipFilters?.includes("sales") && (
                            <Grid item xs={6} sm={3}>
                                <FormControl fullWidth margin="normal">
                                    <CustomerAutoComplete
                                        key={localFilter.customerNo}
                                        valueId={localFilter.customerNo}
                                        onChange={(selected) => handleFilterChange("customerNo", selected?.value || "")}
                                        infoText={t("help.filterSalesCustomer")}
                                    />

                                </FormControl>
                            </Grid>
                        )}
                        <Grid item xs={6} sm={3}>
                            <FormControl fullWidth margin="normal">
                                <SupplierAutoComplete
                                    key={localFilter.supplierNo}
                                    valueId={localFilter.supplierNo}
                                    onChange={(selected) => handleFilterChange("supplierNo", selected?.value || "")}
                                    infoText={t("help.filterSalesSupplier")}
                                />

                            </FormControl>
                        </Grid>
                        {!skipFilters?.includes("sales") && (
                            <>
                            <Grid item xs={6} sm={3}>
                                <FormControl fullWidth margin="normal">
                                        <ShopAutoComplete
                                            key={localFilter.shop}
                                            valueId={localFilter.shop}
                                        onChange={(selected) => handleFilterChange("shop", selected?.value || "")}
                                        infoText={t("help.filterSalesShop")}
                                    />

                                </FormControl>
                            </Grid>
                       
                        <Grid item xs={6} sm={3}>
                            <FormControl fullWidth margin="normal">
                                        <SalespersonAutoComplete
                                            valueId={localFilter.salesperson}
                                            key={localFilter.salesperson}
                                    onChange={(selected) => handleFilterChange("salesperson", selected?.value || "")}
                                    infoText={t("help.filterSalesSalesperson")}
                                />

                            </FormControl>
                                </Grid>
                        </>
                        )}
                        <Grid item xs={6} sm={3}>
                            <FormControl fullWidth margin="normal">
                                <LocationAutoComplete
                                    key={localFilter.location}
                                    valueId={localFilter.location}
                                    onChange={(selected) => handleFilterChange("location", selected?.value || "")}
                                    infoText={t("help.filterLocation")}
                                />

                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <FormControl fullWidth margin="normal">
                                <CountryAutoComplete
                                    key={localFilter.country}
                                    valueId={localFilter.country}
                                    onChange={(selected) => handleFilterChange("country", selected?.value || "")}
                                    infoText={t("help.filterSalesCountry")}
                                />

                            </FormControl>
                        </Grid>

                        <Grid item xs={6} sm={3}>
                            <FormControl fullWidth margin="normal">
                                <ItemAutoComplete
                                    key={localFilter.itemNo}
                                    valueId={localFilter.itemNo}
                                    onChange={(selected) => handleFilterChange("itemNo", selected?.value || "")}
                                    infoText={t("help.filterItem")}
                                />

                            </FormControl>
                        </Grid>

                        <Grid item xs={6} sm={3}>
                            <FormControl fullWidth margin="normal">
                                <ItemGroupAutoComplete
                                    key={localFilter.itemGroup}
                                    valueId={localFilter.itemGroup}
                                    onChange={(selected) => handleFilterChange("itemGroup", selected?.value || "")}
                                    infoText={"Filter sales for a specific Item Group"}
                                />

                            </FormControl>
                        </Grid>
             
                        <ItemCategoryProductGroupSelect
                            key={`${localFilter?.itemCategory ?? ""}::${localFilter?.productGroup ?? ""}`}
                                itemCategoryCode={localFilter.itemCategory}
                                productGroupCode={localFilter.productGroup}
                                onChange={(cat, group) =>
                                    handleChanges({ itemCategory: cat, productGroup: group })
                                }
                                wrapItem={(node) => (
                                    <Grid item xs={6} sm={3}>
                                        {node}
                                    </Grid>
                                )}
                            />
                           
                    </Grid>
                </Box>
            )}

            <Grid item xs={12} alignItems="center" sx={{ mt: 1 }} >
                <Button variant="contained" color="secondary" fullWidth onClick={handleClear}>
                    {t("common.clearFilter")}
                </Button>
            </Grid>
            <Grid container spacing={2} alignItems="center" sx={{mt:1} }>
                <Grid item xs={12}>
                    <Button variant="contained" color="success" fullWidth onClick={handleGenerate}>
                        {t("common.generateReport")}
                    </Button>
                </Grid>
            </Grid>
        
        </Box>
    );
};

export default ReportHeader;
